import type { ListTasksInput, TaskSummary } from '@fos/shared/contracts/work';
import { SquareCheckBig } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { useAcceptRequest, useTaskMutations, useToday } from '../hooks/useTasks';
import { dueInfo } from '../lib/due';
import {
  AcceptRequestButton,
  AiStatus,
  AssigneeAvatar,
  DelegatedLabel,
  DoneToggle,
  DueText,
  FromClientBadge,
  isDelegatedByMe,
  PriorityFlag,
  RequestMark,
  TaskChips,
  TaskStatusLabel,
  useMeId,
} from './parts';

/**
 * Lista zadań (te same dane i filtry co tablica). Desktop: kolumny Zadanie / Status / Termin / Wykonawca.
 * Mobile (Figma 04 Zadania / Mobile, 3037:4999): kółko, tytuł, chipy klienta i projektu, termin, status.
 * Cały wiersz prowadzi do karty zadania; kółko oznacza zadanie jako gotowe (od razu, z „Cofnij”). Zgłoszenie od klienta
 * (status „Zgłoszenie”) zamiast kółka ma „Przyjmij” (jak na tablicy; przegląd portalu 17.10.2026).
 *
 * Lifting 17.10.2026 (DECISIONS „Zadania łatwiejsze w użyciu”, „Lekki lifting UI”): chipy klienta (stała barwa)
 * i projektu, status jako pigułka, termin po czasie na czerwono / dziś na bursztynowo, flaga tylko dla wysokiego
 * i pilnego priorytetu, wiersz z tłem przy najechaniu.
 */

type ListInput = NonNullable<ListTasksInput>;

const TABLE_COLUMNS = 'grid-cols-[minmax(0,1fr)_136px_128px_168px]';

export function TaskList({
  input,
  variant = 'table',
  emptyTitle = 'Brak zadań',
  emptyDescription = 'Nic tu nie ma dla wybranych filtrów.',
  emptyAction,
}: {
  input: ListInput;
  variant?: 'table' | 'mobile';
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
}) {
  const list = trpc.tasks.list.useQuery(input);
  const today = useToday();
  const me = useMeId();
  const { toggleDone } = useTaskMutations();
  const acceptRequest = useAcceptRequest();

  if (list.isPending) return <ListSkeleton rows={5} />;
  if (list.isError) return <ErrorState onRetry={() => void list.refetch()} retrying={list.isFetching} />;
  if (list.data.items.length === 0) {
    return <EmptyState icon={SquareCheckBig} title={emptyTitle} description={emptyDescription} action={emptyAction} />;
  }

  return (
    <div>
      {variant === 'table' && (
        <div
          aria-hidden
          className={cn(
            'grid h-9 items-center gap-4 border-b border-line text-caption font-medium text-fg-muted md:-mx-3 md:px-3',
            TABLE_COLUMNS,
          )}
        >
          <span className="pl-9">Zadanie</span>
          <span>Status</span>
          <span>Termin</span>
          <span>Wykonawca</span>
        </div>
      )}
      <ul aria-label="Zadania">
        {list.data.items.map((task) =>
          variant === 'table' ? (
            <TableRow
              key={task.id}
              task={task}
              today={today}
              me={me}
              onToggle={() => toggleDone(task)}
              onAccept={() => acceptRequest(task)}
            />
          ) : (
            <CompactRow
              key={task.id}
              task={task}
              today={today}
              onToggle={() => toggleDone(task)}
              onAccept={() => acceptRequest(task)}
              large
              me={me}
              showAssignee
            />
          ),
        )}
      </ul>
      {list.data.truncated && (
        <p className="py-3 text-caption text-fg-muted">
          Widać {list.data.items.length} zadań. Zawęź filtry, aby zobaczyć resztę.
        </p>
      )}
    </div>
  );
}

const LINK_OVERLAY =
  'outline-none after:absolute after:inset-0 focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-accent';

function TableRow({
  task,
  today,
  me,
  onToggle,
  onAccept,
}: {
  task: TaskSummary;
  today: string;
  me: string | null;
  onToggle: () => void;
  onAccept: () => void;
}) {
  const done = task.status === 'done';
  const request = task.status === 'request';
  const due = dueInfo(task.dueDate, today, { done });
  const delegated = isDelegatedByMe(task, me);
  const fromClient = task.origin === 'client_portal';
  const hasMeta = Boolean(task.client || task.project || task.service || task.aiState !== 'none' || fromClient);
  return (
    <li
      className={cn(
        'relative grid min-h-[56px] items-center gap-4 border-b border-line py-2 transition-colors hover:bg-surface md:-mx-3 md:px-3',
        TABLE_COLUMNS,
      )}
    >
      <span className="flex min-w-0 items-center gap-3 pl-1">
        <span className="relative z-10 flex">
          {request ? (
            <RequestMark />
          ) : (
            <DoneToggle done={done} onToggle={onToggle} disabled={!task.canEdit} title={task.title} />
          )}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex min-w-0 items-center gap-1.5">
            <Link
              to={`/zadania/${task.id}`}
              className={cn(
                'truncate text-body-s font-medium text-fg',
                LINK_OVERLAY,
                done && 'text-fg-muted line-through',
              )}
            >
              {task.title}
            </Link>
            <PriorityFlag priority={task.priority} />
          </span>
          {hasMeta && (
            <span className="flex min-w-0 items-center gap-1.5">
              <FromClientBadge task={task} />
              <TaskChips task={task} className="min-w-0" />
              <AiStatus state={task.aiState} />
            </span>
          )}
        </span>
        {request && task.canEdit && <AcceptRequestButton task={task} onAccept={onAccept} />}
      </span>
      <span className="flex min-w-0">
        <TaskStatusLabel status={task.status} />
      </span>
      <span className="text-body-s text-fg-secondary">
        {due ? <DueText due={due} /> : <span className="text-fg-muted">—</span>}
      </span>
      <span className="flex min-w-0 items-center gap-2 text-body-s text-fg-secondary">
        <AssigneeAvatar assignee={task.assignee} />
        <span className="flex min-w-0 flex-col">
          <span className={cn('truncate', !task.assignee && 'text-fg-muted')}>
            {task.assignee?.name ?? 'Bez osoby'}
          </span>
          {delegated && <DelegatedLabel />}
        </span>
      </span>
    </li>
  );
}

/** Wiersz kompaktowy (lista na telefonie, Dziś): tytuł, pod nim chipy, termin, status i stan AI. */
export function CompactRow({
  task,
  today,
  onToggle,
  onAccept,
  large = false,
  showStatus = true,
  showAssignee = false,
  me = null,
}: {
  task: TaskSummary;
  today: string;
  onToggle?: () => void;
  /** „Przyjmij” przy zgłoszeniu od klienta (zamiast kółka „Gotowe”; przegląd portalu 17.10.2026) */
  onAccept?: () => void;
  /** mobile: tytuł Body/M, wiersz ~74 px */
  large?: boolean;
  /** pigułka statusu, gdy inny niż „Do zrobienia” */
  showStatus?: boolean;
  /** awatar osoby po prawej (lista Zadań na telefonie; 10.10.2026) */
  showAssignee?: boolean;
  /** zalogowana osoba — etykieta „zlecone” */
  me?: string | null;
}) {
  const done = task.status === 'done';
  const request = task.status === 'request';
  const due = dueInfo(task.dueDate, today, { done });
  return (
    <li
      className={cn(
        'relative flex items-start gap-3.5 border-b border-line transition-colors hover:bg-surface active:bg-surface',
        large ? 'min-h-[74px] py-3.5' : 'min-h-[52px] py-2.5',
      )}
    >
      <span className={cn('relative z-10 flex', large ? 'mt-0.5' : 'mt-px')}>
        {request ? (
          <RequestMark size={large ? 20 : 18} />
        ) : (
          <DoneToggle
            done={done}
            onToggle={onToggle}
            disabled={!task.canEdit}
            title={task.title}
            size={large ? 20 : 18}
          />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="flex min-w-0 items-start gap-1.5">
          <Link
            to={`/zadania/${task.id}`}
            className={cn(
              'min-w-0 text-fg',
              LINK_OVERLAY,
              large ? 'line-clamp-3 text-body-m leading-snug break-words' : 'truncate text-body-s',
              done && 'text-fg-muted line-through',
            )}
          >
            {task.title}
          </Link>
          <PriorityFlag priority={task.priority} className={large ? 'mt-1' : 'mt-0.5'} />
        </span>
        <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-caption text-fg-muted">
          <FromClientBadge task={task} />
          <TaskChips task={task} wrap className="max-w-full" />
          {due && <DueText due={due} />}
          {showStatus && task.status !== 'todo' && <TaskStatusLabel status={task.status} />}
          <AiStatus state={task.aiState} />
          {showAssignee && isDelegatedByMe(task, me) && <DelegatedLabel />}
        </span>
      </span>
      {request && onAccept && task.canEdit ? (
        <AcceptRequestButton task={task} onAccept={onAccept} className={large ? 'mt-0.5' : undefined} />
      ) : (
        showAssignee && <AssigneeAvatar assignee={task.assignee} className={large ? 'mt-0.5' : undefined} />
      )}
    </li>
  );
}
